/**
 * How typed values read: the one abbreviation list, the display rule for
 * a one-line value, and name case for a name, place, role, relationship
 * or insurance carrier.
 *
 * Values are stored as typed. Where a one-line value is shown it reads
 * through `displayValue`: a first capital, and an abbreviation typed all
 * in lower case reads in its standard form, but only in the fields of its
 * own group:
 *   - timing and release forms (prn, bid, er, dr, ...) in a medication's
 *     name, dose and directions ('medication');
 *   - conditions (htn, copd, ...) in a condition's name ('diagnosis') and
 *     a medication's reason ('medication_reason');
 *   - tests and measures (egfr, a1c, ...) in a condition's name and a lab
 *     or test name ('lab');
 *   - pt and ot in an appointment's purpose ('appointment_purpose');
 *   - nkda in an allergy's name ('allergy');
 *   - every other one-line value ('value') takes a first capital only.
 * A token typed with any capital ("Dr", "Tia", "PRN") is never changed,
 * and a token followed by a full stop ("dr.") is never an abbreviation.
 *
 * `nameCase` cases a value typed all in lower case: each word takes a
 * capital, credentials read upper-cased (RN, MD, MSW, PhD), a place's
 * facility types (ALF, SNF) and "PO" before "Box" read upper-cased, a
 * relationship's legal words (POA, HCP) do, and a carrier's insurance
 * names (AARP, BCBS) do. Small words (of, at, the) stay lower case after
 * the first word, and so do a person's name particles (van, der, de, la).
 * Mc, O' and D' take their inner capital. A value typed with any capital
 * is returned unchanged.
 *
 * Labels built from stored tokens read every entry through the 'token'
 * scope. QD, QOD and HS are shown only when someone typed them.
 *
 * This file lives in two byte-identical copies, one per package;
 * tools/lint-value-case-mirror.sh keeps them so.
 */

export type DictionaryGroup =
  | 'timing'
  | 'release'
  | 'test'
  | 'condition'
  | 'care'
  | 'legal'
  | 'insurance'
  | 'allergy';

export type NameCaseKind = 'person' | 'place' | 'role' | 'relationship' | 'carrier';

export type DisplayScope =
  | 'value'
  | 'medication'
  | 'medication_reason'
  | 'diagnosis'
  | 'lab'
  | 'appointment_purpose'
  | 'allergy';

export type DictionaryScope = NameCaseKind | DisplayScope | 'token';

export interface Abbreviation {
  /** Lower case, as matched: 'egfr'. */
  typed: string;
  /** How it reads: 'eGFR'. */
  standard: string;
  group: DictionaryGroup;
  /** A professional credential (RN, MD, MSW, ...): read in every name kind. */
  credential: boolean;
  /** A facility type (ALF, SNF, ...): read in a place. */
  facility: boolean;
  /** Read in an appointment's purpose (PT, OT). */
  purpose: boolean;
}

const CREDENTIALS: ReadonlySet<string> = new Set([
  'md', 'np', 'rn', 'lpn', 'cna', 'lcsw', 'msw', 'lsw', 'pa', 'phd', 'dpt', 'otr', 'ccm', 'lpc', 'lmft',
]);
const FACILITY_TYPES: ReadonlySet<string> = new Set(['alf', 'snf', 'ltc', 'icu', 'hha', 'dme']);
const PURPOSE_ENTRIES: ReadonlySet<string> = new Set(['pt', 'ot']);

function entry(typed: string, standard: string, group: DictionaryGroup): Abbreviation {
  return {
    typed,
    standard,
    group,
    credential: CREDENTIALS.has(typed),
    facility: FACILITY_TYPES.has(typed),
    purpose: PURPOSE_ENTRIES.has(typed),
  };
}

export const ABBREVIATIONS: readonly Abbreviation[] = [
  // When and how a medication is taken.
  entry('prn', 'PRN', 'timing'),
  entry('bid', 'BID', 'timing'),
  entry('tid', 'TID', 'timing'),
  entry('qid', 'QID', 'timing'),
  entry('qd', 'QD', 'timing'),
  entry('qod', 'QOD', 'timing'),
  entry('hs', 'HS', 'timing'),
  entry('po', 'PO', 'timing'),
  entry('sl', 'SL', 'timing'),
  entry('iv', 'IV', 'timing'),
  entry('ml', 'mL', 'timing'),
  // Release forms in a medication's name.
  entry('er', 'ER', 'release'),
  entry('xr', 'XR', 'release'),
  entry('xl', 'XL', 'release'),
  entry('cr', 'CR', 'release'),
  entry('sr', 'SR', 'release'),
  entry('dr', 'DR', 'release'),
  entry('odt', 'ODT', 'release'),
  // Tests and measures.
  entry('egfr', 'eGFR', 'test'),
  entry('a1c', 'A1C', 'test'),
  entry('inr', 'INR', 'test'),
  entry('bp', 'BP', 'test'),
  entry('cbc', 'CBC', 'test'),
  entry('bmp', 'BMP', 'test'),
  entry('cmp', 'CMP', 'test'),
  entry('tsh', 'TSH', 'test'),
  entry('ekg', 'EKG', 'test'),
  entry('ecg', 'ECG', 'test'),
  entry('mri', 'MRI', 'test'),
  entry('bmi', 'BMI', 'test'),
  entry('ct', 'CT', 'test'),
  // Conditions.
  entry('copd', 'COPD', 'condition'),
  entry('chf', 'CHF', 'condition'),
  entry('ckd', 'CKD', 'condition'),
  entry('uti', 'UTI', 'condition'),
  entry('dvt', 'DVT', 'condition'),
  entry('cva', 'CVA', 'condition'),
  entry('htn', 'HTN', 'condition'),
  entry('cad', 'CAD', 'condition'),
  entry('afib', 'AFib', 'condition'),
  entry('gerd', 'GERD', 'condition'),
  entry('osa', 'OSA', 'condition'),
  entry('tia', 'TIA', 'condition'),
  entry('mi', 'MI', 'condition'),
  // Care, services and people.
  entry('adl', 'ADL', 'care'),
  entry('adls', 'ADLs', 'care'),
  entry('iadl', 'IADL', 'care'),
  entry('iadls', 'IADLs', 'care'),
  entry('pt', 'PT', 'care'),
  entry('ot', 'OT', 'care'),
  entry('st', 'ST', 'care'),
  entry('dme', 'DME', 'care'),
  entry('snf', 'SNF', 'care'),
  entry('alf', 'ALF', 'care'),
  entry('icu', 'ICU', 'care'),
  entry('ltc', 'LTC', 'care'),
  entry('pcp', 'PCP', 'care'),
  entry('hha', 'HHA', 'care'),
  entry('md', 'MD', 'care'),
  entry('np', 'NP', 'care'),
  entry('rn', 'RN', 'care'),
  entry('lpn', 'LPN', 'care'),
  entry('cna', 'CNA', 'care'),
  entry('lcsw', 'LCSW', 'care'),
  // Legal and wishes.
  entry('dnr', 'DNR', 'legal'),
  entry('dni', 'DNI', 'legal'),
  entry('dnh', 'DNH', 'legal'),
  entry('polst', 'POLST', 'legal'),
  entry('poa', 'POA', 'legal'),
  entry('dpoa', 'DPOA', 'legal'),
  entry('hcp', 'HCP', 'legal'),
  // Insurance.
  entry('hmo', 'HMO', 'insurance'),
  entry('ppo', 'PPO', 'insurance'),
  entry('aarp', 'AARP', 'insurance'),
  entry('bcbs', 'BCBS', 'insurance'),
  entry('uhc', 'UHC', 'insurance'),
  // Further credentials, tests, conditions and the allergy entry.
  entry('msw', 'MSW', 'care'),
  entry('lsw', 'LSW', 'care'),
  entry('pa', 'PA', 'care'),
  entry('phd', 'PhD', 'care'),
  entry('dpt', 'DPT', 'care'),
  entry('otr', 'OTR', 'care'),
  entry('ccm', 'CCM', 'care'),
  entry('lpc', 'LPC', 'care'),
  entry('lmft', 'LMFT', 'care'),
  entry('hba1c', 'HbA1c', 'test'),
  entry('covid', 'COVID', 'condition'),
  entry('nkda', 'NKDA', 'allergy'),
];

const BY_TYPED: ReadonlyMap<string, Abbreviation> = new Map(ABBREVIATIONS.map((e) => [e.typed, e]));

/** A person's name particles: lower case after the first word. */
export const NAME_PARTICLES: readonly string[] = [
  'van', 'von', 'der', 'den', 'de', 'del', 'della', 'di', 'da', 'du', 'la', 'le', 'dos', 'das', 'bin',
  'ibn', 'al',
];
const PARTICLES: ReadonlySet<string> = new Set(NAME_PARTICLES);

/** Small words: lower case after the first word of a name. */
const SMALL_WORDS: ReadonlySet<string> = new Set([
  'a', 'an', 'and', 'as', 'at', 'by', 'for', 'in', 'of', 'on', 'or', 'the', 'to',
]);

/** Whether an entry reads in this scope: each group in its own fields. */
function allowedIn(e: Abbreviation, scope: DictionaryScope, nextWord: string | undefined): boolean {
  switch (scope) {
    case 'token':
      return true;
    case 'value':
      return false;
    case 'medication':
      return e.group === 'timing' || e.group === 'release';
    case 'medication_reason':
      return e.group === 'condition';
    case 'diagnosis':
      return e.group === 'condition' || e.group === 'test';
    case 'lab':
      return e.group === 'test';
    case 'appointment_purpose':
      return e.purpose;
    case 'allergy':
      return e.group === 'allergy';
    case 'person':
    case 'role':
      return e.credential;
    case 'place':
      return e.credential || e.facility || (e.typed === 'po' && nextWord === 'box');
    case 'relationship':
      return e.credential || e.group === 'legal';
    case 'carrier':
      return e.credential || e.group === 'insurance';
  }
}

/**
 * The standard form of a word in a scope, or null. A word matches only
 * when typed entirely in lower case, and only where its group applies.
 * `nextWord` is the next word, letters only: "po" reads PO in a place
 * only before "box".
 */
export function abbreviationFor(word: string, scope: DictionaryScope, nextWord?: string): string | null {
  if (word !== word.toLowerCase()) return null;
  const e = BY_TYPED.get(word);
  if (e === undefined) return null;
  return allowedIn(e, scope, nextWord) ? e.standard : null;
}

const LEAD_RE = /^[^\p{L}\p{N}]*/u;
const CORE_RE = /^[\p{L}\p{N}']*/u;

function upperFirstChar(s: string): string {
  if (s.length === 0) return s;
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** One part of a name (a word, or a hyphen- or slash-separated piece of one). */
function casePart(part: string, kind: NameCaseKind, isFirst: boolean, nextWord: string | undefined): string {
  const lead = LEAD_RE.exec(part)?.[0] ?? '';
  const afterLead = part.slice(lead.length);
  const core = CORE_RE.exec(afterLead)?.[0] ?? '';
  const rest = afterLead.slice(core.length);

  if (core.length > 0 && !rest.startsWith('.')) {
    const form = abbreviationFor(core, kind, nextWord);
    if (form !== null) return lead + form + rest;
  }

  if (!isFirst && (SMALL_WORDS.has(core) || (kind === 'person' && PARTICLES.has(core)))) {
    return part;
  }

  if (/^\p{N}/u.test(core)) return part;

  let cased: string;
  if (core === 'ii' || core === 'iii' || core === 'iv') {
    cased = core.toUpperCase();
  } else if (/^mc\p{L}/u.test(core)) {
    cased = 'Mc' + upperFirstChar(core.slice(2));
  } else if (/^[od]'\p{L}/u.test(core)) {
    cased = core.charAt(0).toUpperCase() + "'" + upperFirstChar(core.slice(2));
  } else {
    cased = upperFirstChar(core);
  }
  // A letter straight after a full stop inside the part takes a capital too.
  const restCased = rest.replace(/\.(\p{L})/gu, (_m, c: string) => '.' + c.toUpperCase());
  return lead + cased + restCased;
}

/**
 * Name case for a value typed all in lower case. A value with any capital
 * is returned unchanged. Whitespace, hyphens and slashes are kept.
 */
export function nameCase(value: string, kind: NameCaseKind): string {
  if (value.length === 0) return value;
  if (value !== value.toLowerCase()) return value;

  const chunks = value.split(/(\s+)/);
  const words = chunks.filter((c, i) => i % 2 === 0 && c.length > 0);
  const nextWordAfter = (wordIndex: number): string | undefined => {
    const next = words[wordIndex + 1];
    return next === undefined ? undefined : next.replace(/[^\p{L}]/gu, '');
  };

  let wordIndex = -1;
  let isFirst = true;
  return chunks
    .map((chunk, i) => {
      if (i % 2 === 1 || chunk.length === 0) return chunk;
      wordIndex += 1;
      const nextWord = nextWordAfter(wordIndex);
      return chunk
        .split(/([-/])/)
        .map((piece, j) => {
          if (j % 2 === 1 || piece.length === 0) return piece;
          const cased = casePart(piece, kind, isFirst, nextWord);
          isFirst = false;
          return cased;
        })
        .join('');
    })
    .join('');
}

const TOKEN_RE = /[\p{L}\p{N}']+/gu;

/**
 * How a one-line value reads: its scope's abbreviations in their standard
 * form, then a first capital, unless the first word was replaced, starts
 * with a digit, carries a capital of its own after its first letter, or
 * is an email address or a web address.
 */
export function displayValue(value: string, scope: DisplayScope = 'value'): string {
  if (value.length === 0) return value;

  let firstTokenSeen = false;
  let capitalise = true;

  const out = value
    .split(/(\s+)/)
    .map((chunk, i) => {
      if (i % 2 === 1 || chunk.length === 0) return chunk;
      if (chunk.includes('@') || chunk.includes('://')) {
        if (!firstTokenSeen && TOKEN_RE.test(chunk)) {
          firstTokenSeen = true;
          capitalise = false;
        }
        TOKEN_RE.lastIndex = 0;
        return chunk;
      }
      return chunk.replace(TOKEN_RE, (token: string, offset: number, whole: string) => {
        const followedByStop = whole.charAt(offset + token.length) === '.';
        const form = followedByStop ? null : abbreviationFor(token, scope);
        if (!firstTokenSeen) {
          firstTokenSeen = true;
          if (form !== null || /^\p{N}/u.test(token) || /\p{Lu}/u.test(token.slice(1))) {
            capitalise = false;
          }
        }
        return form ?? token;
      });
    })
    .join('');

  if (!capitalise) return out;
  const at = out.search(/\p{L}/u);
  if (at === -1) return out;
  const up = out.charAt(at).toUpperCase();
  return up === out.charAt(at) ? out : out.slice(0, at) + up + out.slice(at + 1);
}
